import {
  matchHitlResponseAccepted,
  type AgentInProgressData,
  type HitlAnswer,
  type HitlResponseAcceptedDetail,
} from '@auw/workspace-utils';

export type HitlSubmissionRejection =
  | { kind: 'preflight' }
  | {
      kind: 'server_terminal_before_ack';
      terminalStatus: 'completed' | 'failed';
    }
  | { kind: 'transport_before_ack' }
  | { kind: 'aborted_before_ack' }
  | { kind: 'stream_ended_before_ack' };

export interface HitlSubmissionLifecycleCallbacks {
  onAccepted?: (receipt: HitlResponseAcceptedDetail) => void;
  onRejected?: (rejection: HitlSubmissionRejection) => void;
}

export interface HitlSubmissionLifecycleController {
  observe: (data: AgentInProgressData) => boolean;
  reject: (rejection: HitlSubmissionRejection) => boolean;
  isPending: () => boolean;
}

type Settlement = 'pending' | 'accepted' | 'rejected';

function notifySafely<T>(callback: ((value: T) => void) | undefined, value: T): void {
  if (!callback) return;
  try {
    callback(value);
  } catch (error) {
    // A presentation callback must never turn an accepted server request into
    // an apparent transport failure or interrupt the response stream.
    console.error('[HITL] submission lifecycle callback failed', error);
  }
}

/**
 * One-shot settlement controller for a submitted HITL continuation.
 *
 * It accepts only the explicit post-reservation receipt whose identifiers
 * match the submitted answer. Event order, response text, and HTTP status are
 * deliberately absent from the acceptance decision.
 */
export function createHitlSubmissionLifecycle(
  submittedAnswer: HitlAnswer,
  callbacks?: HitlSubmissionLifecycleCallbacks,
): HitlSubmissionLifecycleController {
  let settlement: Settlement = 'pending';

  return {
    observe(data) {
      if (settlement !== 'pending') return false;
      const receipt = matchHitlResponseAccepted(data, submittedAnswer);
      if (!receipt) return false;
      settlement = 'accepted';
      notifySafely(callbacks?.onAccepted, receipt);
      return true;
    },

    reject(rejection) {
      if (settlement !== 'pending') return false;
      settlement = 'rejected';
      notifySafely(callbacks?.onRejected, rejection);
      return true;
    },

    isPending() {
      return settlement === 'pending';
    },
  };
}
